import { useCallback, useEffect, useRef, useState } from 'react'
import { StreamPlayer, type TrackMeta } from './StreamPlayer'
import { StreamingPlayer } from './player/StreamingPlayer'
import { SeparationEngine, type DownloadState, type ModelInfo } from './engine'
import { decodeAudio, encodeWav } from './dsp/audio'
import { MDX } from './dsp/separator'

interface Output {
  url: string
  bytes: number
}

const TRACKS: TrackMeta[] = [
  { id: 'original', label: 'Original', hint: 'The uploaded mix, decoded to 44.1 kHz stereo.' },
  {
    id: 'instrumental',
    label: 'Instrumental',
    hint: 'What the model predicts. Streams chunk by chunk — playable long before separation finishes.',
  },
  {
    id: 'vocals',
    label: 'Vocals',
    hint: 'The residual, mix − instrumental. Free: no second model pass, just a subtraction.',
  },
]

export default function App() {
  const engineRef = useRef<SeparationEngine | null>(null)
  const urlsRef = useRef<string[]>([])
  const playerRef = useRef<StreamingPlayer | null>(null)
  // Player state lives outside React; bump this to pull re-renders through.
  const [playerVersion, setPlayerVersion] = useState(0)

  const [modelInfo, setModelInfo] = useState<ModelInfo | null>(null)
  const [modelError, setModelError] = useState<string | null>(null)
  const [download, setDownload] = useState<DownloadState | null>(null)

  const [fileName, setFileName] = useState<string | null>(null)
  const [duration, setDuration] = useState(0)
  const [instFile, setInstFile] = useState<Output | null>(null)
  const [vocFile, setVocFile] = useState<Output | null>(null)
  const [runMs, setRunMs] = useState(0)
  const [progress, setProgress] = useState<{ chunk: number; total: number } | null>(null)
  const [ready, setReady] = useState(0)
  const [firstMs, setFirstMs] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  // App only mounts after the user clicks Start on the landing page; begin
  // fetching + compiling the model immediately from that point, so it is warm
  // by the time the user has picked a file.
  useEffect(() => {
    const engine = new SeparationEngine()
    engineRef.current = engine
    console.log('[compare] loading model...')
    engine
      .load(setDownload)
      .then((info) => {
        console.log('[compare] model ready on', info.ep, info)
        setModelInfo(info)
      })
      .catch((e: Error) => {
        console.error('[compare] model load failed:', e)
        setModelError(e.message)
      })
    const player = new StreamingPlayer(MDX.sampleRate, () => setPlayerVersion((v) => v + 1))
    playerRef.current = player

    return () => {
      engine.dispose()
      player.dispose()
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u))
      urlsRef.current = []
    }
  }, [])

  const publish = useCallback((ch0: Float32Array, ch1: Float32Array, sampleRate: number): Output => {
    const blob = encodeWav(ch0, ch1, sampleRate)
    const url = URL.createObjectURL(blob)
    urlsRef.current.push(url)
    return { url, bytes: blob.size }
  }, [])

  const handleFile = useCallback(
    async (file: File) => {
      if (busy) return
      if (!modelInfo) {
        setError('model is still loading — try again once it finishes')
        return
      }
      setBusy(true)
      setError(null)
      setInstFile(null)
      setVocFile(null)
      setProgress(null)
      setReady(0)
      setFirstMs(0)
      setRunMs(0)
      setFileName(file.name)
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u))
      urlsRef.current = []

      const player = playerRef.current
      player?.reset()

      try {
        console.log('[compare] decoding', file.name, file.size, 'bytes')
        const t0 = performance.now()
        const { ch0, ch1, sampleRate } = await decodeAudio(file, MDX.sampleRate)
        console.log(`[compare] decoded in ${(performance.now() - t0).toFixed(0)}ms: ${ch0.length} samples`)
        setDuration(ch0.length / sampleRate)
        player?.setTrack('original', { ch0, ch1, ready: ch0.length })

        const engine = engineRef.current
        if (!engine) throw new Error('engine not initialised')

        // Settled chunks are copied into full-length buffers as they arrive, so
        // both stems become playable long before separation finishes.
        const inst = { ch0: new Float32Array(ch0.length), ch1: new Float32Array(ch1.length), ready: 0 }
        const voc = { ch0: new Float32Array(ch0.length), ch1: new Float32Array(ch1.length), ready: 0 }
        const t1 = performance.now()
        let firstSegmentAt = 0

        const result = await engine.run(
          ch0,
          ch1,
          sampleRate,
          (chunk, total) => setProgress({ chunk, total }),
          (seg) => {
            inst.ch0.set(seg.inst0, seg.start)
            inst.ch1.set(seg.inst1, seg.start)
            voc.ch0.set(seg.voc0, seg.start)
            voc.ch1.set(seg.voc1, seg.start)
            inst.ready = voc.ready = seg.start + seg.inst0.length
            if (!firstSegmentAt) {
              firstSegmentAt = performance.now() - t1
              console.log(`[compare] first audio playable after ${firstSegmentAt.toFixed(0)}ms`)
              setFirstMs(firstSegmentAt)
            }
            player?.setTrack('instrumental', inst)
            player?.setTrack('vocals', voc)
            setReady(inst.ready / sampleRate)
          },
        )
        console.log(`[compare] separation done in ${result.ms.toFixed(0)}ms`)
        setRunMs(result.ms)
        setInstFile(publish(result.inst0, result.inst1, sampleRate))
        setVocFile(publish(result.voc0, result.voc1, sampleRate))
        // Swap in the properly peak-normalised final versions for playback.
        player?.setTrack('instrumental', {
          ch0: result.inst0,
          ch1: result.inst1,
          ready: result.inst0.length,
        })
        player?.setTrack('vocals', { ch0: result.voc0, ch1: result.voc1, ready: result.voc0.length })
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        console.error('[compare] processing failed:', e)
        setError(msg)
      } finally {
        setBusy(false)
        setProgress(null)
      }
    },
    [busy, modelInfo, publish],
  )

  const player = playerRef.current
  const done = instFile !== null

  return (
    <div className="page">
      <h1>Vocal separation, in the browser</h1>
      <p className="sub">
        No backend. The file is decoded, separated and played back entirely in this tab — the ONNX
        model runs client-side via onnxruntime-web.
      </p>

      <ModelStatus info={modelInfo} error={modelError} download={download} />

      <div
        className={`drop${dragging ? ' drag' : ''}${!modelInfo ? ' disabled' : ''}`}
        onDragOver={(e) => {
          e.preventDefault()
          if (modelInfo) setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          const f = e.dataTransfer.files[0]
          if (f) void handleFile(f)
        }}
        onClick={() => modelInfo && document.getElementById('file-input')?.click()}
      >
        {!modelInfo
          ? 'Loading model…'
          : busy
            ? 'Processing…'
            : fileName
              ? `${fileName} — drop another to redo`
              : 'Drop an audio file here, or click to choose'}
        <input
          id="file-input"
          type="file"
          accept="audio/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void handleFile(f)
            e.target.value = ''
          }}
        />
      </div>

      {error && <div className="err">Failed: {error}</div>}

      {player && player.hasTrack('original') && (
        <StreamPlayer player={player} tracks={TRACKS} version={playerVersion} />
      )}

      <div className="card">
        <h3>
          MDX-NET fp16
          <span className="badge ai">
            {modelInfo ? `${(modelInfo.modelBytes / 1e6).toFixed(0)} MB · ${modelInfo.ep}` : '33 MB'}
          </span>
        </h3>
        <div className="meta">
          {done ? (
            <>
              {(runMs / 1000).toFixed(2)}s
              {duration > 0 && ` · ${(duration / (runMs / 1000)).toFixed(1)}× realtime`}
              {' · '}
              <a href={instFile.url} download="instrumental.wav">
                instrumental.wav
              </a>
              {vocFile && (
                <>
                  {' · '}
                  <a href={vocFile.url} download="vocals.wav">
                    vocals.wav
                  </a>
                </>
              )}
            </>
          ) : progress ? (
            <>
              chunk {progress.chunk} / {progress.total}
              {ready > 0 && (
                <>
                  {' · '}
                  <strong>{ready.toFixed(1)}s playable</strong>
                  {firstMs > 0 && ` · first audio after ${(firstMs / 1000).toFixed(1)}s`}
                </>
              )}
              <div className="bar">
                <div className="bar-fill" style={{ width: `${(progress.chunk / progress.total) * 100}%` }} />
              </div>
            </>
          ) : (
            'waiting for a file'
          )}
        </div>
        <div className="note">
          One pass produces both stems: the model predicts the instrumental, and the vocals are the
          residual (mix − instrumental) — a subtraction, not a second inference. Each pass settles
          ~4.4s of audio, so playback starts while the rest is still separating.
        </div>
      </div>
    </div>
  )
}

function ModelStatus({
  info,
  error,
  download,
}: {
  info: ModelInfo | null
  error: string | null
  download: DownloadState | null
}) {
  if (error) return <div className="err">Model failed to load: {error}</div>

  if (info) {
    return (
      <div className="status ok">
        Model ready on <strong>{info.ep}</strong> · {(info.modelBytes / 1e6).toFixed(1)} MB{' '}
        {info.fromCache ? 'from cache' : 'fetched'} in {(info.downloadMs / 1000).toFixed(1)}s · session{' '}
        {(info.sessionMs / 1000).toFixed(1)}s · shader warmup {(info.warmupMs / 1000).toFixed(1)}s
      </div>
    )
  }

  const pct = download && download.total ? (download.loaded / download.total) * 100 : null
  return (
    <div className="status">
      {pct === null
        ? download
          ? 'Building session and compiling GPU shaders…'
          : 'Loading model…'
        : `Loading model… ${(download!.loaded / 1e6).toFixed(1)} / ${(download!.total / 1e6).toFixed(1)} MB`}
      {pct !== null && (
        <div className="bar">
          <div className="bar-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  )
}
